import React, { useCallback, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import type { OpaqueVideoData } from '@lumen5/luminary';

import { VideoRenderStatusEnum, VideoStatus } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { SPINNER_ICON, TertiaryButton } from 'js/src/components/common/ui/Button';
import type { ReadonlyPublicVideo } from 'js/src/data/share-video';
import { copyTextToClipboard } from 'js/src/libs/custom-utils';

import { buildRecipeRequest } from './recipeRequest';

const RENDERING_STATUSES: ReadonlySet<VideoStatus> = new Set([
    VideoStatus.QUEUED,
    VideoStatus.PROCESSING,
]);

/**
 * The signed link to the video's render, as the share page already has it. Null for a video with
 * several renders (a series), which the author attaches as an MP4 instead.
 */
function getRenderUrl(video: ReadonlyPublicVideo): string | null {
    const finished = video.video_renders.filter(
        (render) => render.status === VideoRenderStatusEnum.FINISHED && render.https_url,
    );
    return finished.length === 1 ? finished[0].https_url : null;
}

/**
 * The video's state as the toolbox agent sees a reference video, plus every change made on top of
 * each scene design, built from the share page's own copy of the video so it works for any video
 * the viewer can see (the per-workspace video endpoints don't). It's built in a throwaway store with
 * the editor's full state shape, since the share page's store lacks the slices the serializer and
 * linter read. The share page has no transcript editors, so narration comes from the saved words.
 */
async function describeVideoForRecipe(video: ReadonlyPublicVideo): Promise<string | null> {
    const [
        { describeReferenceVideo },
        { buildReferenceStateGetter },
        { getRootReducer },
        { createReduxStore },
        { loadTemplateBundle },
        { getTemplateBundle },
    ] = await Promise.all([
        import('js/src/agent/tools/reference-video-annotation'),
        import('js/src/agent/tools/reference-video-transcript'),
        import('js/src/entry-points/root-reducers/sketch-reducers'),
        import('js/src/libs/redux-utils'),
        import('js/src/data/templates/templates-operations'),
        import('js/src/data/templates/templates-selectors'),
    ]);
    const videoData = cloneDeep(video.video_data) as OpaqueVideoData & {
        template?: { id?: string };
    };
    const templateId = videoData.template?.id;
    const store = createReduxStore(getRootReducer());
    let templateBundle: ReturnType<typeof getTemplateBundle> = null;
    if (templateId) {
        try {
            await store.dispatch(loadTemplateBundle(templateId));
            templateBundle = getTemplateBundle(store.getState(), templateId);
        } catch (error) {
            // A template the viewer can't load costs the design-default markers, not the request.
            console.warn('[create-recipe] could not load the template bundle', error); // eslint-disable-line no-console
        }
    }
    return describeReferenceVideo(
        videoData,
        buildReferenceStateGetter(store.getState(), undefined),
        templateBundle,
        { includeDesignChanges: true },
    );
}

interface CreateRecipeButtonProps {
    video: ReadonlyPublicVideo;
}

/**
 * Staff-only. Copies a ready-to-paste request for the Claude Agent to build a toolbox recipe from this
 * video with the recipe-kitchen skill: the video's state and a signed link to its published render,
 * which the agent takes a frame of each scene from. Lives on the share page because that's where a
 * video is published; opening it in the editor puts it back to draft.
 */
const CreateRecipeButton: React.FC<CreateRecipeButtonProps> = ({ video }) => {
    const [isWorking, setIsWorking] = useState(false);

    const handleClick = useCallback(async () => {
        if (isWorking) {
            return;
        }
        if (RENDERING_STATUSES.has(video.status)) {
            Toast.info('This video is still rendering. Try again once it has published.');
            return;
        }
        if (video.status !== VideoStatus.PUBLISHED) {
            Toast.error(
                'Publish this video first. Claude takes a frame of every scene from the published render.',
            );
            return;
        }
        setIsWorking(true);
        try {
            const videoState = await describeVideoForRecipe(video);
            if (videoState === null) {
                Toast.error('This video has no scenes to build a recipe from');
                return;
            }
            const renderUrl = getRenderUrl(video);
            await copyTextToClipboard(
                buildRecipeRequest({
                    videoId: video.id,
                    videoName: video.name || 'Untitled video',
                    renderUrl,
                    videoState,
                }),
            );
            Toast.success(
                renderUrl
                    ? 'Recipe request copied. Paste it into a Slack thread and @mention Claude Agent.'
                    : "Recipe request copied. Paste it into a Slack thread, attach the video's MP4, and @mention Claude Agent.",
            );
        } catch (error) {
            console.error('Error creating recipe request:', error); // eslint-disable-line no-console
            Toast.error('Error creating the recipe request');
        } finally {
            setIsWorking(false);
        }
    }, [isWorking, video]);

    return (
        <TertiaryButton
            text={isWorking ? 'Preparing…' : '👨‍🍳 Create recipe'}
            onClick={handleClick}
            disabled={isWorking}
            iconLeft={isWorking ? SPINNER_ICON : undefined}
            className="w-100"
        />
    );
};

export default CreateRecipeButton;
